import { useState, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import './ActionableInsightsContainer.css';
import DataTable from './DataTable';
import ContainerBox from './ContainerBox';

const SECTION_HEADING_REGEX = /^\s{0,3}#{1,6}\s+(.+?)\s*$/;
const MARKDOWN_EMPHASIS_REGEX = /(\*\*|__)/g;
const MODEL_QUALITY_VALUE_REGEX = /\b(subpar|acceptable|good|excellent)\b/i;
const QUALITY_SECTION_TITLES = ['quality statement', 'resulting quality'];

const QUALITY_BADGE_TONE_BY_LEVEL = {
  subpar: 'error',
  acceptable: 'warning',
  good: 'success',
  excellent: 'success'
};

const QUALITY_LABEL_BY_LEVEL = {
  subpar: 'Subpar',
  acceptable: 'Acceptable',
  good: 'Good',
  excellent: 'Excellent'
};

const stripMarkdownFormatting = (value = '') => value.replace(MARKDOWN_EMPHASIS_REGEX, '').trim();

const parseLabeledLine = (line) => {
  const cleanedLine = stripMarkdownFormatting(line).trim();
  const separatorIndex = cleanedLine.indexOf(':');

  if (separatorIndex === -1) {
    return null;
  }

  return {
    label: cleanedLine.slice(0, separatorIndex).trim(),
    value: cleanedLine.slice(separatorIndex + 1).trim()
  };
};

const formatPercentage = (value) => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const roundedWhole = Math.round(value);
  const maximumFractionDigits = Math.abs(value - roundedWhole) < 0.05 || value >= 10 ? 0 : 1;

  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value)}%`;
};

const formatMetricValue = (label, value) => {
  const cleanedValue = stripMarkdownFormatting(value).replace(/[.]$/, '').trim();

  if (!cleanedValue) {
    return '';
  }

  if (cleanedValue.includes('%')) {
    return cleanedValue;
  }

  const numericValue = Number(cleanedValue.replace(/,/g, ''));

  if (!Number.isFinite(numericValue)) {
    return cleanedValue;
  }

  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes('accuracy')) {
    return formatPercentage(numericValue <= 1 ? numericValue * 100 : numericValue);
  }

  if (normalizedLabel.includes('mape') || normalizedLabel.includes('deviation')) {
    return formatPercentage(numericValue);
  }

  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(numericValue);
};

const parseResultingQualityLines = (lines) => {
  const parsedData = {
    qualityLevel: null,
    metricLabel: '',
    metricValue: '',
    explanation: '',
    extraMarkdown: ''
  };
  const extraLines = [];

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      return;
    }

    const parsedLine = parseLabeledLine(trimmedLine);

    if (!parsedLine) {
      extraLines.push(line);
      return;
    }

    const normalizedLabel = parsedLine.label.toLowerCase();

    if (normalizedLabel === 'model quality') {
      const qualityMatch = parsedLine.value.match(MODEL_QUALITY_VALUE_REGEX);
      parsedData.qualityLevel = qualityMatch?.[1]?.toLowerCase() || null;
      return;
    }

    if (normalizedLabel.startsWith('test ')) {
      parsedData.metricLabel = parsedLine.label;
      parsedData.metricValue = formatMetricValue(parsedLine.label, parsedLine.value);
      return;
    }

    if (normalizedLabel === 'explanation') {
      parsedData.explanation = parsedLine.value;
      return;
    }

    extraLines.push(line);
  });

  parsedData.extraMarkdown = extraLines.join('\n').trim();
  return parsedData;
};

const getSectionRange = (headingIndexes, sectionIndex, totalLines) => {
  if (sectionIndex === -1) {
    return null;
  }

  const nextHeadingIndex = headingIndexes.find((headingIndex) => headingIndex > sectionIndex);

  return {
    start: sectionIndex + 1,
    end: nextHeadingIndex ?? totalLines
  };
};

const extractSummaryDisplayData = (summary) => {
  const safeSummary = typeof summary === 'string' ? summary.trim() : '';

  if (!safeSummary) {
    return {
      hasStructuredSummary: false,
      qualityLevel: null,
      metricLabel: '',
      metricValue: '',
      explanation: '',
      qualityExtraMarkdown: '',
      remainingMarkdown: '',
      fallbackMarkdown: ''
    };
  }

  const lines = safeSummary.split(/\r?\n/);
  const headings = lines
    .map((line, index) => {
      const match = line.match(SECTION_HEADING_REGEX);
      return match ? { index, title: stripMarkdownFormatting(match[1]).toLowerCase() } : null;
    })
    .filter(Boolean);
  const headingIndexes = headings.map((heading) => heading.index);
  const qualityHeading = headings.find((heading) => QUALITY_SECTION_TITLES.includes(heading.title));
  const qualityRange = getSectionRange(headingIndexes, qualityHeading?.index ?? -1, lines.length);

  if (!qualityRange) {
    const fallbackQualityData = parseResultingQualityLines(lines);

    return {
      hasStructuredSummary: false,
      qualityLevel: fallbackQualityData.qualityLevel,
      metricLabel: fallbackQualityData.metricLabel,
      metricValue: fallbackQualityData.metricValue,
      explanation: fallbackQualityData.explanation,
      qualityExtraMarkdown: '',
      remainingMarkdown: '',
      fallbackMarkdown: safeSummary
    };
  }

  const qualityLines = lines.slice(qualityRange.start, qualityRange.end);
  const qualityData = parseResultingQualityLines(qualityLines);
  const remainingMarkdown = [
    ...lines.slice(0, qualityHeading.index),
    ...lines.slice(qualityRange.end)
  ]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return {
    hasStructuredSummary: true,
    qualityLevel: qualityData.qualityLevel,
    metricLabel: qualityData.metricLabel,
    metricValue: qualityData.metricValue,
    explanation: qualityData.explanation,
    qualityExtraMarkdown: qualityData.extraMarkdown,
    remainingMarkdown,
    fallbackMarkdown: ''
  };
};

const ActionableInsightsContainer = ({
  visible,
  analysis,
  selectedTarget,
  onReadyClick
}) => {
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Generate insights
  const generateInsights = useCallback(async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    setLoading(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.generateInsights(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      if (response.data.result) {
        setInsightsData(response.data.result);
        setIsStale(false);
      }
    } catch (err) {
      console.error('Error generating insights:', err);
      setError(err.response?.data?.detail || 'Failed to generate insights. Please ensure you have trained a model and calculated SHAP values.');
    } finally {
      setLoading(false);
    }
  }, [analysis?.datasets, selectedTarget?.name]);

  // Load insights data and indicate whether generation is needed
  const loadInsightsData = useCallback(async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) {
      return { shouldGenerate: false };
    }

    setLoading(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getInsightsStatus(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      if (response.data.insights_exist && response.data.status === 'ready') {
        setInsightsData(response.data.data);
        setIsStale(false);
        return { shouldGenerate: false };
      }

      if (response.data.status === 'stale') {
        setIsStale(true);
        setInsightsData(response.data.data);
        return { shouldGenerate: false };
      }

      // No insights exist yet or they are not ready – trigger generation
      setInsightsData(null);
      setIsStale(false);
      return { shouldGenerate: true };
    } catch (err) {
      console.error('Error loading insights:', err);
      // If insights don't exist, fall back to generation
      setInsightsData(null);
      setIsStale(false);
      return { shouldGenerate: true };
    } finally {
      setLoading(false);
    }
  }, [analysis?.datasets, selectedTarget?.name]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    setRefreshing(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');

      // Refresh insights artifacts
      await apiService.refreshInsightsArtifacts(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      // Reload results
      await loadInsightsData();

      // Clear stale flag
      setIsStale(false);
    } catch (err) {
      setError('Failed to refresh insights. Please try again.');
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  }, [analysis?.datasets, selectedTarget?.name, loadInsightsData]);

  // Load insights when component becomes visible or target changes
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && selectedTarget?.name) {
      // Reset state when target changes
      setInsightsData(null);
      setError(null);
      setIsStale(false);

      const run = async () => {
        const { shouldGenerate } = await loadInsightsData();
        if (shouldGenerate) {
          await generateInsights();
        }
      };

      run();
    }
  }, [visible, analysis?.datasets, selectedTarget?.name, loadInsightsData, generateInsights]);

  const {
    hasStructuredSummary,
    qualityLevel,
    metricLabel,
    metricValue,
    explanation,
    qualityExtraMarkdown,
    remainingMarkdown,
    fallbackMarkdown
  } = useMemo(
    () => extractSummaryDisplayData(insightsData?.summary),
    [insightsData?.summary]
  );

  if (!visible) return null;

  return (
    <ContainerBox
      title="Actionable Insights & Quality Statement"
      isReady={false}
      scrollToNext={false}
      isStale={isStale}
      onRefreshClick={handleRefresh}
      refreshing={refreshing}
      onReadyClick={onReadyClick}
    >
      {/* Loading state */}
      {loading && !insightsData && (
        <div className="insights-loading">
          <div className="loading-spinner"></div>
          <p>Generating actionable insights...</p>
        </div>
      )}

      {/* Error state (no data) */}
      {error && !insightsData && (
        <div className="insights-error">
          <p>{error}</p>
        </div>
      )}

      {/* Empty state (no insights yet) */}
      {!insightsData && !loading && !error && (
        <div className="insights-empty-state">
          <p>Generating actionable insights to understand how your model results can add business value.</p>
        </div>
      )}

      {/* Error banner (with data) */}
      {error && insightsData && (
        <div className="insights-error">
          <p>{error}</p>
        </div>
      )}

      {/* Summary Section */}
      {insightsData && (
        <>
          <div className="insights-section">
            <div className="insights-summary">
              {hasStructuredSummary ? (
                <>
                  <div className="insights-summary__section">
                    <h3>Quality Statement</h3>
                    {qualityLevel && (
                      <p className="insights-summary__meta-row">
                        <span className="insights-summary__meta-label">Model quality:</span>{' '}
                        <span className={`insights-summary__quality-level insights-summary__quality-level--${QUALITY_BADGE_TONE_BY_LEVEL[qualityLevel]}`}>
                          {QUALITY_LABEL_BY_LEVEL[qualityLevel]}
                        </span>
                      </p>
                    )}
                    {metricLabel && metricValue && (
                      <p className="insights-summary__meta-row">
                        <span className="insights-summary__meta-label">{metricLabel}:</span>{' '}
                        <span className="insights-summary__meta-value">{metricValue}</span>
                      </p>
                    )}
                    {explanation && (
                      <p className="insights-summary__meta-row insights-summary__meta-row--multiline">
                        <span className="insights-summary__meta-label">Explanation:</span>{' '}
                        <span className="insights-summary__meta-text">{explanation}</span>
                      </p>
                    )}
                    {qualityExtraMarkdown && (
                      <div className="insights-summary__markdown q-md">
                        <ReactMarkdown>{qualityExtraMarkdown}</ReactMarkdown>
                      </div>
                    )}
                  </div>

                  {remainingMarkdown && (
                    <div className="insights-summary__markdown q-md">
                      <ReactMarkdown>{remainingMarkdown}</ReactMarkdown>
                    </div>
                  )}
                </>
              ) : fallbackMarkdown && (
                <div className="insights-summary__markdown q-md">
                  <ReactMarkdown>{fallbackMarkdown}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>

          {/* Inference Preview Table */}
          {insightsData.inference_preview && (
            <div className="insights-section-preview">
              <h3>Top Predictions</h3>
              <p className="insights-section-description">
                {insightsData.task_type === 'classification'
                  ? `Showing rows predicted as class "${insightsData.target_class_of_interest}"`
                  : `Showing rows sorted by predicted ${insightsData.target_column} (${insightsData.sort_direction})`
                }
              </p>
              <DataTable
                columns={insightsData.inference_preview.columns}
                rows={insightsData.inference_preview.rows}
                maxRows={20}
              />
            </div>
          )}

          {/* When no separate inference preview is available, we simply omit this block.
              Predictions are now surfaced directly in Section 4 (Model Training). */}
        </>
      )}
    </ContainerBox>
  );
};

export default ActionableInsightsContainer;

